import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Eye, ExternalLink, Search, Video, Clock, CheckCircle, Edit } from "lucide-react";
import { formatJamaicaDate as formatDate } from "@/lib/datetime";

interface CreatorVideo {
  id: string;
  partnerId: string;
  videoUrl: string;
  platform: string;
  currentViews: number;
  milestonesHit: number;
  milestonesEarned: number;
  conversions: number;
  status: string;
  adminNotes: string | null;
  submittedAt: string;
  expiresAt: string;
  lastViewUpdate: string | null;
  partner: {
    firstName: string;
    lastName: string;
    email: string;
    referralCode: string;
  };
}

export default function AdminCreatorVideos() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState<CreatorVideo | null>(null);
  const [editViews, setEditViews] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const { data, isLoading } = useQuery<{ videos: CreatorVideo[] }>({
    queryKey: ["/api/admin/creator-videos"],
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, currentViews, adminNotes }: { id: string; currentViews: number; adminNotes?: string }) => {
      return await apiRequest("PATCH", `/api/admin/creator-videos/${id}`, {
        currentViews,
        adminNotes: adminNotes || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/creator-videos"] });
      toast({ title: "Video updated", description: "View count and milestones have been updated." });
      setSelectedVideo(null);
    },
    onError: (error: any) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const allVideos = data?.videos || [];
  const filtered = allVideos.filter((v) => {
    const matchesStatus = statusFilter === "all" || v.status === statusFilter;
    const matchesSearch = searchQuery === "" ||
      `${v.partner.firstName} ${v.partner.lastName} ${v.partner.email} ${v.videoUrl}`.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const counts = {
    all: allVideos.length,
    active: allVideos.filter(v => v.status === 'active').length,
    expired: allVideos.filter(v => v.status === 'expired').length,
  };

  const totalViews = allVideos.reduce((sum, v) => sum + v.currentViews, 0);
  const totalMilestonesEarned = allVideos.reduce((sum, v) => sum + v.milestonesEarned, 0);

  const formatNumber = (n: number) => n.toLocaleString();

  const getDaysRemaining = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const openEdit = (video: CreatorVideo) => {
    setSelectedVideo(video);
    setEditViews(String(video.currentViews));
    setEditNotes(video.adminNotes || "");
  };

  const handleSaveViews = () => {
    if (!selectedVideo) return;
    const views = parseInt(editViews, 10);
    if (isNaN(views) || views < 0) {
      toast({ title: "Invalid views", description: "Please enter a valid number.", variant: "destructive" });
      return;
    }
    updateMutation.mutate({ id: selectedVideo.id, currentViews: views, adminNotes: editNotes || undefined });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Creator Videos</h1>
        <p className="text-muted-foreground">Review submitted videos and update view counts</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Videos</CardTitle>
            <Video className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.all}</div>
            <p className="text-xs text-muted-foreground">{counts.active} active, {counts.expired} expired</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalViews)}</div>
            <p className="text-xs text-muted-foreground">Across all videos</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">View Earnings</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${formatNumber(totalMilestonesEarned)} JMD</div>
            <p className="text-xs text-muted-foreground">$1,000 per 10k views</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {(['all', 'active', 'expired'] as const).map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s]})
            </Button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by creator or URL..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading videos...</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 gap-2">
            <Video className="w-8 h-8 text-muted-foreground" />
            <p className="text-muted-foreground">No videos found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((video) => (
            <Card key={video.id}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1 flex-1 min-w-[250px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground">
                        {video.partner.firstName} {video.partner.lastName}
                      </h3>
                      <Badge variant="secondary">
                        {video.platform === 'tiktok' ? 'TikTok' : 'Instagram'}
                      </Badge>
                      {video.status === 'active' ? (
                        <Badge variant="secondary" className="bg-chart-2/10 text-chart-2">
                          <Clock className="w-3 h-3 mr-1" />
                          {getDaysRemaining(video.expiresAt)}d left
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Expired</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{video.partner.email}</p>
                    <a
                      href={video.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span className="truncate max-w-[400px]">{video.videoUrl}</span>
                    </a>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                      <span className="text-foreground">
                        <strong>{formatNumber(video.currentViews)}</strong> views
                      </span>
                      <span className="text-foreground">
                        <strong>{video.milestonesHit}</strong> milestones
                      </span>
                      <span className="text-foreground">
                        <strong>${formatNumber(video.milestonesEarned)}</strong> JMD earned
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Submitted: {formatDate(video.submittedAt)}
                      {video.lastViewUpdate && ` | Last updated: ${formatDate(video.lastViewUpdate)}`}
                    </p>
                    {video.adminNotes && (
                      <p className="text-xs text-muted-foreground mt-1">Notes: {video.adminNotes}</p>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => openEdit(video)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Update Views
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selectedVideo} onOpenChange={(open) => !open && setSelectedVideo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Video Views</DialogTitle>
            <DialogDescription>
              {selectedVideo?.partner.firstName} {selectedVideo?.partner.lastName} - {selectedVideo?.platform === 'tiktok' ? 'TikTok' : 'Instagram'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground">Video URL</Label>
              <a
                href={selectedVideo?.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1 mt-1"
              >
                <ExternalLink className="w-3 h-3" />
                Open video
              </a>
            </div>
            <div className="space-y-2">
              <Label htmlFor="viewCount">Current View Count</Label>
              <Input
                id="viewCount"
                type="number"
                min="0"
                placeholder="Enter current view count..."
                value={editViews}
                onChange={(e) => setEditViews(e.target.value)}
              />
              {editViews && !isNaN(parseInt(editViews)) && (
                <p className="text-xs text-muted-foreground">
                  This will set milestones to {Math.floor(parseInt(editViews) / 10000)} (${formatNumber(Math.floor(parseInt(editViews) / 10000) * 1000)} JMD)
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="adminNotes">Admin Notes (Optional)</Label>
              <Textarea
                id="adminNotes"
                placeholder="Add notes about this video..."
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedVideo(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveViews} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
